/**
 * Collection of UI that is used throughout the styleguide. Some of this can
 * maybe eventually be moved to base/ui, but a lot of it is specific to the
 * styleguide, or is mostly just a convenience type to wrap some styling
 * or variants of existing UI components.
 */
import { Button } from 'base/ui/button';
import {
  IconGoogle,
  IconLock,
  IconMonotype,
  IconStars,
  IconThumbsUp,
  IconThumbsUpFilled,
  IconUnlock,
} from 'base/ui/icons';
import React from 'react';

import { writeToClipboard } from 'base/clipboard';
import { UnreachableError } from 'base/helpers';
import { CompiledChromaticColor } from 'base/styleguide/types';
import { Inline, Rows } from 'base/ui/layout';
import { ToastQueue } from 'base/ui/toast';
import { FontFamilyMetadata } from 'blocks/blocks_service';
import classNames from 'classnames';
import { GetColorName } from 'hex-color-to-color-name';
import { HexColorPicker } from 'react-colorful';
import styles from './ui.css';
import './ui.global.css';

export function getIconForFontSource(source: FontFamilyMetadata['source']) {
  switch (source) {
    case 'M':
      return <IconMonotype />;
    case 'G':
      return <IconGoogle />;
    default:
      throw new UnreachableError(source);
  }
}

export function SegmentedFooter({ children }: { children: React.ReactNode[] }) {
  const sections: React.ReactNode[] = [];
  children = children.filter((c) => c);
  for (let i = 0; i < children.filter((c) => c).length; i++) {
    if (i > 0) {
      sections.push(
        <div key={i}>
          <div className={styles.separator} />
          {children[i]}
        </div>,
      );
    } else {
      sections.push(children[i]);
    }
  }
  return <div className={styles.segmentedFooter}>{sections}</div>;
}

export function LikeButton({
  variant,
  onClick,
  toggled,
}: {
  variant: 'subtle' | 'secondary';
  onClick: () => void;
  toggled: boolean;
}) {
  const styles = {};
  const onClickWithSupress = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };
  return (
    <div style={styles}>
      <Button
        size="small"
        variant={variant}
        iconColor="ai"
        icon={toggled ? IconThumbsUpFilled : IconThumbsUp}
        onClick={onClickWithSupress}
        tooltip={toggled ? 'Remove from liked' : 'Add to liked'}
      />
    </div>
  );
}

/**
 * Generic vertical card component.
 *
 * Usage:
 * <Card>
 *    <CardContent>...</CardContent>
 *    <CardFooter>...</CardFooter>
 * </Card>
 */
export function Card({ children }: { children: React.ReactNode }) {
  return <div className={styles.card}>{children}</div>;
}

export interface SelectableProperties {
  /** Function that will run when the card is selected. */
  onSelect: () => void;
  /** Function that will run when the card is deselected. */
  onDeselect: () => void;
  /**
   * Optional labels that are displayed as chips on top of the card content.
   * The `selected` chip will be displayed when the card is in it's selected
   * state. The `deselected` chip will display on hover when the card is in
   * it's deselected state.
   */
  chipLabels: { selected: string | null; deselected: string | null };
}

/**
 * Selectable variant of the card content panel. Will run the callbacks
 * provided on selection/deselection, and optionally render a chip message
 * on hover/selection.
 */
export function SelectableCardContent({
  children,
  selectableProperties,
}: {
  children: React.ReactNode;
  selectableProperties: SelectableProperties;
}) {
  const [selected, setSelected] = React.useState(false);
  const cardSelected = React.useCallback(() => {
    const newSelectedState = !selected;
    setSelected(newSelectedState);
    newSelectedState
      ? selectableProperties.onSelect()
      : selectableProperties.onDeselect();
  }, [selected, selectableProperties]);
  return (
    <div className={styles.selectableContainer} onClick={cardSelected}>
      <div
        className={classNames(
          styles.overlay,
          selected && styles.selectedOverlay,
        )}
      >
        {selected && (
          <div className={styles.selectedChip}>
            {selectableProperties.chipLabels?.selected && (
              <Button
                iconColor="ai"
                icon={IconStars}
                label={selectableProperties.chipLabels.selected}
              />
            )}
          </div>
        )}
        <div className={styles.hoverChip}>
          {!selected && selectableProperties.chipLabels?.deselected && (
            <Button label={selectableProperties.chipLabels.deselected} />
          )}
        </div>
      </div>
      <div className={styles.cardContent}>{children}</div>
    </div>
  );
}
export function CardContent({ children }: { children: React.ReactNode }) {
  return <div className={styles.cardContent}>{children}</div>;
}

export function CardFooter({ children }: { children: React.ReactNode }) {
  return <div className={styles.cardFooter}>{children}</div>;
}

export function LockButton({
  locked,
  onLockClick,
}: {
  locked: boolean;
  onLockClick: (toLock: boolean) => void;
}) {
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLockClick(!locked);
  };
  return (
    <div className={styles.lockIcon}>
      <Button
        icon={locked ? IconLock : IconUnlock}
        variant="subtle"
        tooltip={locked ? 'Unlock' : 'Lock'}
        onClick={onClick}
      />
    </div>
  );
}

/** The visible popup panel of the color picker. */
export function ColorPickerPanel({
  color,
  onChange,
}: {
  color: string;
  onChange: (color: string, source: 'text' | 'visual') => void;
}) {
  const processColorInput = (value: string) => {
    onChange(value, 'visual');
  };
  return (
    <Rows spacing={16}>
      <div className={'use-react-colorful-overrides'}>
        <HexColorPicker color={color} onChange={processColorInput} />
      </div>
    </Rows>
  );
}

/** Helper function to copy a hex color to the clipboard and show a toast notification. */
export function copyColorToClipboard(
  toastQueue: ToastQueue,
  opts: { color: string; name?: string },
) {
  const clipboardSwatch = (
    <div
      className={styles.clipboardSwatch}
      style={{ backgroundColor: opts.color }}
    />
  );
  writeToClipboard({
    'text/plain': opts.color,
  });
  toastQueue.push({
    label: `${opts.name ?? GetColorName(opts.color)} copied to clipboard`,
    customLeft: clipboardSwatch,
  });
}

export function ShadeSwatches({
  color,
  textColor,
  toastQueue,
}: {
  color: CompiledChromaticColor;
  textColor: string;
  toastQueue: ToastQueue;
}) {
  return (
    <div className={styles.shades} style={{ color: textColor }}>
      <Inline spacing={0}>
        {color.shades.map((shade, i) => (
          <div
            key={i}
            className={styles.shade}
            style={{ background: shade }}
            onClick={(e) => {
              e.preventDefault();
              copyColorToClipboard(toastQueue, {
                color: shade,
                name: color.name,
              });
            }}
          >
            {shade === color.hex && <div className={styles.shadeKnob}></div>}
          </div>
        ))}
      </Inline>
    </div>
  );
}
